import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { KmContextSource } from '@/entities/expert-report-km';
import { NestedItems } from '@/features/nested-items';
import { KmContextAttributeTitle } from './KmContextAttributeTitle';
import { useFormatKmContextSources } from './useFormatKmContextSources';
import type { KmContextAttributeOption } from './types';

type KmOwnContextListProps = {
  title: string;
  value: KmContextAttributeOption[];
  /** Всё, из чего собираются строки: свои атрибуты и справочник для выбора */
  allItems: KmContextAttributeOption[];
  /** Справочник для выбора загружается */
  isAllItemsLoading: boolean;
  /** Уровни выше, с которых приходит и свой атрибут, — по коду атрибута-повтора */
  upperSourcesByCode: Map<string, KmContextSource[]>;
  onChange: (items: KmContextAttributeOption[]) => void;
  disabled: boolean;
  /** Справочник для выбора открыт пользователю: без него свои атрибуты только снимаются */
  canAdd: boolean;
};

const getOptionCode = (item: KmContextAttributeOption) => item.code;

const matchesSearch = (item: KmContextAttributeOption, searchQuery: string) => {
  const query = searchQuery.toLowerCase();
  return item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query);
};

/** Контекст оценки, назначенный самому уровню заключения, с выбором атрибутов из справочника */
export const KmOwnContextList: FC<KmOwnContextListProps> = ({
  title,
  value,
  allItems,
  isAllItemsLoading,
  upperSourcesByCode,
  onChange,
  disabled,
  canAdd,
}) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const formatSources = useFormatKmContextSources();

  const renderTitle = (item: KmContextAttributeOption) => (
    <KmContextAttributeTitle name={item.name} isRepeat={upperSourcesByCode.has(item.code)} />
  );

  const renderDescription = (item: KmContextAttributeOption) => {
    const upperSources = upperSourcesByCode.get(item.code);
    if (!upperSources) return item.description;
    return t('expertReport:km.contextRepeatSources', { sources: formatSources(upperSources) });
  };

  return (
    <NestedItems<KmContextAttributeOption>
      labels={{
        title,
        addButton: t('expertReport:km.addContextAttribute'),
        removeButton: t('expertReport:km.removeContextAttribute'),
        emptyState: t('expertReport:km.noOwnContext'),
        dialogTitle: t('expertReport:km.contextDialogTitle'),
        searchPlaceholder: t('common:search'),
        noItemsFound: t('expertReport:km.noContextAttributesFound'),
      }}
      value={value}
      onChange={onChange}
      allItems={allItems}
      isLoading={isAllItemsLoading}
      getItemId={getOptionCode}
      renderTitle={renderTitle}
      renderHint={getOptionCode}
      renderDescription={renderDescription}
      searchFilter={matchesSearch}
      disabled={disabled}
      canAdd={canAdd}
    />
  );
};
